// A tab opened before a deploy still runs the old bundle. Its next lazy route asks for a chunk the
// new release no longer has (hashed names), the import fails, and the route would stay blank.
// Vite reports that as `vite:preloadError`; reload once to pick up the new release
// (docs/RELEASING.md § 3). A guard in sessionStorage stops a reload loop when the chunk is missing
// for another reason (a broken deploy): the second failure within a minute is left to the route's
// error component.

const KEY = 'orca.staleBundleReloadAt'
const WINDOW_MS = 60_000

export interface StaleBundleDeps {
  now: () => number
  storage: Pick<Storage, 'getItem' | 'setItem'> | null
  reload: () => void
}

/** Returns true when it reloaded (and the caller should suppress the error). */
export function handleStaleBundle(deps: StaleBundleDeps): boolean {
  let last: number | null = null
  try {
    const stored = deps.storage?.getItem(KEY)
    last = stored ? Number(stored) : null
  } catch {
    // storage blocked: still reload; the loop guard is best-effort
  }
  if (last !== null && Number.isFinite(last) && deps.now() - last < WINDOW_MS) return false
  try {
    deps.storage?.setItem(KEY, String(deps.now()))
  } catch {
    // ignore
  }
  deps.reload()
  return true
}

export function installStaleBundleReload(target: Window = window): void {
  target.addEventListener('vite:preloadError', (event) => {
    let storage: Storage | null = null
    try {
      storage = target.sessionStorage
    } catch {
      storage = null
    }
    if (handleStaleBundle({ now: Date.now, storage, reload: () => target.location.reload() })) {
      event.preventDefault()
    }
  })
}
